import styled, { css } from 'styled-components';
import { mix, rgba } from 'polished';

import { FieldValidationState } from '@cld/common-definitions';
import { combineValues, globalTheme } from '@cld/component-theme';
import { typographyMixin } from '@cld/typography';
import { supportFieldLineLayout } from '@cld/field-inline-layout';

import { FieldSize } from '../FieldSize';
import { borderColorVariant, textFieldTheme } from '../theme';

export interface FieldBaseProps {
  isDisabled: boolean;
  hasLeadingDecoration: boolean;
  hasTrailingDecoration: boolean;
  isFocused: boolean;
  size: FieldSize;
  validationState: FieldValidationState;
}

export const FieldBase = styled.div<FieldBaseProps>`
  &&& {
    ${(props) => typographyMixin(props.size, 'regular')};
    align-items: center;
    background-color: ${textFieldTheme.background.color.base};
    border: ${textFieldTheme.border.width} solid ${borderColorVariant()};
    border-radius: ${textFieldTheme.border.radius};
    overflow: hidden;
    ${supportFieldLineLayout()};
    color: ${textFieldTheme.color.base};
    display: flex;
    transition: border-color 250ms, box-shadow 250ms;

    ${({ isFocused }) =>
      isFocused &&
      css`
        border-color: ${textFieldTheme.border.color.focus};
        box-shadow: 0 0 0 3px ${combineValues(textFieldTheme.outline.color.base, (color) => rgba(color, 0.5))};
      `}

    ${({ isDisabled }) =>
      isDisabled
        ? css`
            background-color: ${combineValues(textFieldTheme.background.color.base, globalTheme.palette.contrastInvert, (baseColor, contrastColor) =>
              mix(0.5, baseColor, contrastColor)
            )};
            border-color: ${combineValues(textFieldTheme.border.color.base, globalTheme.palette.contrastInvert, (baseColor, contrastColor) =>
              mix(0.5, baseColor, contrastColor)
            )};
            color: ${combineValues(textFieldTheme.color.base, globalTheme.palette.contrastInvert, (baseColor, contrastColor) =>
              mix(0.5, baseColor, contrastColor)
            )};
          `
        : css`
            &:hover {
              border-color: ${textFieldTheme.border.color.hover};
            }
          `}

  input {
      ${({ size }) => typographyMixin(size, 'regular')};
      background: none;
      border: none;

      color: ${textFieldTheme.input.color.base};
      height: 100%;
      width: 100%;

      ${(props) => css`
        padding: ${textFieldTheme.input.padding[props.size]};
      `};

      ${({ hasLeadingDecoration }) =>
        hasLeadingDecoration &&
        css`
          padding-left: ${textFieldTheme.input.decorationMargin};
        `}

      ${({ hasTrailingDecoration }) =>
        hasTrailingDecoration &&
        css`
          padding-right: ${textFieldTheme.input.decorationMargin};
        `}

    &:disabled,
    &::placeholder {
        color: ${textFieldTheme.input.color.placeholder};
      }

      &:focus {
        outline: none;
      }

      //hide (chrome) browser's auto-complete background
      box-shadow: 0 0 0 100px ${textFieldTheme.background.color.base} inset;

      /* bidirectional text handling, The directionality is calculated using the P2 and P3 rules of the Unicode Bidirectional Algorithm  */
      unicode-bidi: plaintext;
    }
  }
`;
